import { useAuthStore } from "../stores/authStore";

const API_BASE_URL = `${import.meta.env.VITE_API_URL || ""}/api`;

export interface ApiErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
  };
}

export class ApiError extends Error {
  code: string;
  status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
  }
}

// リフレッシュ競合防止: 同時に複数の401が来ても1回だけリフレッシュする
let refreshPromise: Promise<boolean> | null = null;

interface TokenPair {
  accessToken: string;
  refreshToken: string;
}

async function tryRefreshToken(): Promise<boolean> {
  const { refreshToken, user } = useAuthStore.getState();
  if (!(refreshToken && user)) {
    return false;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      return false;
    }

    const tokenPair = (await response.json()) as TokenPair;
    useAuthStore
      .getState()
      .login(user, tokenPair.accessToken, tokenPair.refreshToken);
    return true;
  } catch {
    return false;
  }
}

function refreshTokenWithDedup(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = tryRefreshToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

async function handleResponse<T>(
  response: Response,
  retryFn?: () => Promise<Response>
): Promise<T> {
  if (response.status === 401) {
    if (retryFn) {
      const refreshed = await refreshTokenWithDedup();
      if (refreshed) {
        const retryResponse = await retryFn();
        return handleResponse<T>(retryResponse);
      }
    }

    useAuthStore.getState().logout();
    window.location.href = "/auth/login";
    throw new ApiError("UNAUTHORIZED", "認証が必要です", 401);
  }

  if (!response.ok) {
    const errorData = (await response.json()) as ApiErrorResponse;
    throw new ApiError(
      errorData.error?.code ?? "UNKNOWN_ERROR",
      errorData.error?.message ?? "エラーが発生しました",
      response.status
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

function getAuthHeaders(): HeadersInit {
  const token = useAuthStore.getState().accessToken;
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

export async function apiGet<T>(
  endpoint: string,
  params?: Record<string, string | undefined>
): Promise<T> {
  const url = new URL(`${API_BASE_URL}${endpoint}`, window.location.origin);

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        url.searchParams.set(key, value);
      }
    }
  }

  const doFetch = () =>
    fetch(url.toString(), {
      method: "GET",
      headers: getAuthHeaders(),
    });

  const response = await doFetch();
  return handleResponse<T>(response, doFetch);
}

export async function apiPost<T, B = unknown>(
  endpoint: string,
  body?: B
): Promise<T> {
  const doFetch = () =>
    fetch(`${API_BASE_URL}${endpoint}`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });

  const response = await doFetch();
  return handleResponse<T>(response, doFetch);
}

export async function apiDelete<T>(endpoint: string): Promise<T> {
  const doFetch = () =>
    fetch(`${API_BASE_URL}${endpoint}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });

  const response = await doFetch();
  return handleResponse<T>(response, doFetch);
}

export async function apiPatch<T, B = unknown>(
  endpoint: string,
  body?: B
): Promise<T> {
  const doFetch = () =>
    fetch(`${API_BASE_URL}${endpoint}`, {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });

  const response = await doFetch();
  return handleResponse<T>(response, doFetch);
}

export async function apiPut<T, B = unknown>(
  endpoint: string,
  body?: B
): Promise<T> {
  const doFetch = () =>
    fetch(`${API_BASE_URL}${endpoint}`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });

  const response = await doFetch();
  return handleResponse<T>(response, doFetch);
}
